import DOMPurify from 'dompurify';
import { marked } from 'marked';

import type {
  DatoRelease,
  ReleaseImage,
  ReleaseVideo,
} from '@src/repositories/ReleasesRepository';

export enum ChangeCategory {
  Feature = 'Feature',
  Improvement = 'Improvement',
  BugFix = 'Bug Fix',
  Other = 'Other',
}

export interface CategorySection {
  type: ChangeCategory;
  label: string;
  html: string;
}

export interface ProcessedRelease {
  id: string;
  name: string;
  publishedAt: string;
  sanitizedHtml: string;
  categories: CategorySection[];
  hasCategories: boolean;
  video: ReleaseVideo | null;
  image: ReleaseImage | null;
}

const CATEGORY_MAP: Record<string, ChangeCategory> = {
  features: ChangeCategory.Feature,
  feature: ChangeCategory.Feature,
  'new features': ChangeCategory.Feature,
  improvements: ChangeCategory.Improvement,
  improvement: ChangeCategory.Improvement,
  enhancements: ChangeCategory.Improvement,
  enhancement: ChangeCategory.Improvement,
  'bug fixes': ChangeCategory.BugFix,
  'bug fix': ChangeCategory.BugFix,
  bugfixes: ChangeCategory.BugFix,
  fixes: ChangeCategory.BugFix,
  fix: ChangeCategory.BugFix,
};

export class ReleaseNotesService {
  static parseBody(markdown: string): string {
    const rawHtml = marked.parse(markdown, { async: false }) as string;
    return DOMPurify.sanitize(rawHtml);
  }

  static categorizeChanges(markdown: string): {
    categories: CategorySection[];
    hasCategories: boolean;
  } {
    const sections = markdown.split(/^## /m);

    if (sections.length <= 1) {
      return { categories: [], hasCategories: false };
    }

    const categories: CategorySection[] = [];

    for (let i = 1; i < sections.length; i++) {
      const section = sections[i];
      const newlineIndex = section.indexOf('\n');
      if (newlineIndex === -1) continue;

      const heading = section.substring(0, newlineIndex).trim();
      const content = section.substring(newlineIndex + 1).trim();
      if (!content) continue;

      const normalizedHeading = heading
        .toLowerCase()
        .replace(/[^a-z\s]/g, '')
        .trim();
      const categoryType =
        CATEGORY_MAP[normalizedHeading] ?? ChangeCategory.Other;
      const html = DOMPurify.sanitize(
        marked.parse(content, { async: false }) as string,
      );

      categories.push({
        type: categoryType,
        label: heading,
        html,
      });
    }

    return { categories, hasCategories: categories.length > 0 };
  }

  static processRelease(release: DatoRelease): ProcessedRelease {
    const description = release.description || '';
    const sanitizedHtml = ReleaseNotesService.parseBody(description);
    const { categories, hasCategories } =
      ReleaseNotesService.categorizeChanges(description);

    return {
      id: release.id,
      name: release.releaseName,
      publishedAt: release._createdAt,
      sanitizedHtml,
      categories,
      hasCategories,
      video: release.video,
      image: release.image,
    };
  }
}
